import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Settings,
  ThumbsUp,
  ArrowRight,
  Share2,
  CheckCircle,
} from "lucide-react";

const Container = styled(Paper)(({ theme }) => ({
  width: "100%",
  margin: "0 auto",
  boxShadow: theme.shadows[6],
  display: "flex",
  flexDirection: "column",
}));
const Header = styled(Box)(({ theme }) => ({
  background: "linear-gradient(90deg, #f97316, #ef4444)",
  color: "#fff",
  padding: theme.spacing(3),
}));

const HeaderTitle = styled(Typography)(() => ({
  fontWeight: 700,
  marginBottom: 4,
}));

const HeaderSubtitle = styled(Typography)(() => ({
  color: "rgba(255,255,255,0.85)",
}));

const Content = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const StepContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const StepHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(1.5),
}));

const StepNumber = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: "50%",
  background: "linear-gradient(135deg, #f97316, #ef4444)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  boxShadow: "0 4px 12px rgba(249, 115, 22, 0.3)",
}));

const StepNumberText = styled(Typography)(() => ({
  color: "#fff",
  fontWeight: 700,
  fontSize: "1.25rem",
}));

const StepContent = styled(Box)(() => ({
  flex: 1,
}));

const StepTitle = styled(Typography)(() => ({
  fontWeight: 700,
  color: "#1f2937",
  marginBottom: 4,
}));

const StepDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[600],
  lineHeight: 1.6,
}));

const StepIcon = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: theme.spacing(1.5),
  backgroundColor: "#fff7ed",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
}));

const HighlightBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff7ed",
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  border: "2px solid #fed7aa",
  marginTop: theme.spacing(1),
}));

const HighlightText = styled(Typography)(() => ({
  color: "#9a3412",
  fontWeight: 500,
  fontSize: "0.9rem",
  whiteSpace: "pre-line",
}));

const InfoBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#eff6ff",
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  border: "1px solid #bfdbfe",
  marginTop: theme.spacing(3),
}));

const InfoText = styled(Typography)(() => ({
  color: "#1e40af",
  fontSize: "0.875rem",
  lineHeight: 1.6,
  "& a": {
    color: "#2563eb",
    fontWeight: 600,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

const Footer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderTop: `1px solid ${theme.palette.grey[200]}`,
  backgroundColor: "#fff",
}));

const CheckboxContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const ContinueButton = styled(Button)(({ theme }) => ({
  width: "100%",
  paddingBlock: theme.spacing(1.5),
  borderRadius: theme.spacing(2),
  fontWeight: 700,
  fontSize: "1rem",
  boxShadow: theme.shadows[4],
  textTransform: "none",
  background: "linear-gradient(90deg, #f97316, #ef4444)",
  color: "#fff",
  "&:hover": {
    background: "linear-gradient(90deg, #ea580c, #dc2626)",
    boxShadow: theme.shadows[8],
  },
}));

const ButtonContent = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
}));

const STORAGE_KEY = "hideHowItWorks";

const HowItWorks = () => {
  const navigate = useNavigate();
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleContinue = () => {
    if (dontShowAgain) {
      localStorage.setItem(STORAGE_KEY, "true");
    }

    // Force scroll before navigate
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;

    window.location.href = "/setup";
  };

  const steps = [
    {
      number: 1,
      icon: <Settings size={20} color="#f97316" />,
      title: "Set Your Preferences",
      description:
        "Choose your location, sort preferences, party size (2-4), and filter by chains, open hours, price, or food categories.",
      highlight:
        "⚠️ By default, only open restaurants are shown—toggle this in preferences to see all.",
    },
    {
      number: 2,
      icon: <Share2 size={20} color="#f97316" />,
      title: "Share & Wait in Lobby",
      description:
        "Share your session code via URL, text, or QR code. Wait for all friends to join before moving forward.",
    },
    {
      number: 3,
      icon: <ThumbsUp size={20} color="#f97316" />,
      title: "Match Together",
      description:
        "Everyone sees the same list of restaurants and votes on each one. Tap the ❤️ to like or ✕ to pass. Each card shows the name, distance, price, hours, and more.",
    },
    {
      number: 4,
      icon: <CheckCircle size={20} color="#f97316" />,
      title: "Time to Eat!",
      description:
        "When everyone likes the same restaurant, you've got a match! See the full details including address, phone, website, menu, and delivery/restaurant links.",
    },
  ];

  return (
    <Container>
      <Header>
        <HeaderTitle variant="h5">How It Works</HeaderTitle>
        <HeaderSubtitle variant="body2">
          Find your perfect restaurant in 4 simple steps
        </HeaderSubtitle>
      </Header>

      <Content>
        {steps.map((step, index) => (
          <StepContainer key={index}>
            <StepHeader>
              <StepNumber>
                <StepNumberText>{step.number}</StepNumberText>
              </StepNumber>
              <StepIcon>{step.icon}</StepIcon>
            </StepHeader>
            <StepContent>
              <StepTitle variant="h6">{step.title}</StepTitle>
              <StepDescription variant="body2">
                {step.description}
              </StepDescription>
              {step.highlight && (
                <HighlightBox>
                  <HighlightText>{step.highlight}</HighlightText>
                </HighlightBox>
              )}
            </StepContent>
          </StepContainer>
        ))}

        <InfoBox>
          <InfoText>
            📋 By using Foodie Call, you agree to our{" "}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            . We collect location data and anonymous usage analytics to improve
            your experience.
            <br />
            <br />
            💬 Have feedback or questions?{" "}
            <a
              href="https://forms.gle/your-form-id"
              target="_blank"
              rel="noopener noreferrer"
            >
              Let us know
            </a>
            —we'd love to hear from you!
            <br />
            <br />
            You will always have access to the Privacy Policy and Feedback form
            from the footer on the home page.
          </InfoText>
        </InfoBox>
      </Content>

      <Footer>
        <CheckboxContainer>
          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                sx={{
                  color: "#f97316",
                  "&.Mui-checked": {
                    color: "#f97316",
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: "#6b7280" }}>
                Don't show this again
              </Typography>
            }
          />
        </CheckboxContainer>
        <ContinueButton variant="contained" onClick={handleContinue}>
          <ButtonContent>
            <span>Continue to Setup</span>
            <ArrowRight size={20} />
          </ButtonContent>
        </ContinueButton>
      </Footer>
    </Container>
  );
};

// Helper function to check if user has dismissed the page
export const shouldShowHowItWorks = () => {
  return localStorage.getItem(STORAGE_KEY) !== "true";
};

export default HowItWorks;
